import {
  Box,
  BoxProps,
  Button,
  Link,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import { useFirebaseValue } from "@pathwright/utils"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useBlocksConfig } from "../config/BlocksConfigProvider"
import { BLOCKS_PREPARING_CONTENT_KEY } from "../config/constants"
import { BlocksContextType } from "../types"

const CONTENT_MIGRATION_ROOT = "/cf/content-migration"
const STATUS_COMPLETED = "completed"
const STATUS_FAILED = "failed"
const STATUS_TERMINAL_FAILED = "terminal_failed"
const STATUS_IN_PROGRESS = "in_progress"
const STATUS_PENDING_CONTENT_MIGRATION = "pending_content_migration"
const STATUS_COMPLETIONS_NOT_NEEDED = "not_needed"
const COMPLETIONS_ABSENT_GRACE_MS = 1000
// Only trigger reload when user "had the tab open": content was on screen for this long before we saw migration pending.
const CONTENT_STABLE_BEFORE_MIGRATION_MS = 1500
const ENVIRONMENT_UNAVAILABLE_ERROR_CODE = "BLOCKS_ENVIRONMENT_UNAVAILABLE"
export const STAGING_ENV_UNAVAILABLE_MESSAGE =
  "This document exists on the production version of this Step, but is not available in this environment while migrations are disabled. "
const OVERLAY_CONTENT_MAX_WIDTH = "560px"
const OVERLAY_CONTENT_MIN_HEIGHT = "400px"
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isLikelyFirebaseDocumentID = (id?: string | null): boolean =>
  !!id && !UUID_RE.test(id)

const resolveMigrationDocumentID = (
  blocksContext: BlocksContextType | null,
  contentID?: string
): string | null => {
  const routeDocumentID = blocksContext?.contentTargetID
  const contentDocumentID = contentID || null
  const routeReferencesFirebase =
    blocksContext?.contentTarget === "firebase" ||
    blocksContext?.contentStatus === "migrating"

  if (isLikelyFirebaseDocumentID(routeDocumentID)) {
    return routeDocumentID || null
  }

  if (
    routeReferencesFirebase &&
    isLikelyFirebaseDocumentID(contentDocumentID)
  ) {
    return contentDocumentID
  }

  return null
}

export type MigrationWatchState = {
  migrationDocumentID: string | null
  isPending: boolean
  isCompleted: boolean
}

const getGraphQLErrorByCode = (error: any, code: string): any | null => {
  if (Array.isArray(error?.graphQLErrors)) {
    const match = error.graphQLErrors.find(
      (graphQLError: any) => graphQLError?.extensions?.code === code
    )
    if (match) return match
  }
  if (error?.extensions?.code === code) return error
  return null
}

export const isStagingEnvironmentUnavailableError = (error: any): boolean =>
  !!getGraphQLErrorByCode(error, ENVIRONMENT_UNAVAILABLE_ERROR_CODE)

export const useRouteTokenVariables = ({
  contextKey,
  contentRouteToken
}: {
  contextKey: string
  contentRouteToken?: string
}) => {
  const [isRouteTokenDisabled, setIsRouteTokenDisabled] = useState(false)

  useEffect(() => {
    setIsRouteTokenDisabled(false)
  }, [contextKey])

  const disableRouteToken = useCallback(() => {
    setIsRouteTokenDisabled(true)
  }, [])

  return {
    routeTokenVariables:
      !isRouteTokenDisabled && contentRouteToken
        ? { routeToken: contentRouteToken }
        : {},
    disableRouteToken
  }
}

export const useRouteTokenInvalidation = ({
  blocksContext,
  serverContentID,
  migrationNeedsReload,
  disableRouteToken
}: {
  blocksContext: BlocksContextType | null
  serverContentID?: string
  migrationNeedsReload: boolean
  disableRouteToken: () => void
}) => {
  useEffect(() => {
    if (!migrationNeedsReload) return
    disableRouteToken()
  }, [migrationNeedsReload, disableRouteToken])

  useEffect(() => {
    if (
      blocksContext?.contentStatus === "no_content" &&
      blocksContext?.contentTarget === "postgres" &&
      !!serverContentID &&
      UUID_RE.test(serverContentID)
    ) {
      disableRouteToken()
    }
  }, [
    blocksContext?.contentStatus,
    blocksContext?.contentTarget,
    serverContentID,
    disableRouteToken
  ])
}

export const useMigrationSyncerState = ({
  contextKey,
  blocksContext,
  contentID,
  serverContentID,
  contentLoading,
  hasContent,
  disableRouteToken
}: {
  contextKey: string
  blocksContext: BlocksContextType | null
  contentID?: string
  serverContentID?: string
  contentLoading: boolean
  hasContent: boolean
  disableRouteToken: () => void
}) => {
  const migrationWatch = useMigrationWatch({
    blocksContext,
    contentID
  })
  const shouldShowReloadRef = useRef(false)
  const contentStableSinceRef = useRef<number | null>(null)

  useEffect(() => {
    shouldShowReloadRef.current = false
    contentStableSinceRef.current = null
  }, [contextKey, migrationWatch.migrationDocumentID])

  useEffect(() => {
    if (hasContent && !contentLoading) {
      if (contentStableSinceRef.current === null) {
        contentStableSinceRef.current = Date.now()
      }
    } else {
      contentStableSinceRef.current = null
    }
  }, [hasContent, contentLoading])

  useEffect(() => {
    if (!migrationWatch.isPending) return
    const stableSince = contentStableSinceRef.current
    if (
      stableSince !== null &&
      Date.now() - stableSince >= CONTENT_STABLE_BEFORE_MIGRATION_MS
    ) {
      shouldShowReloadRef.current = true
    }
  }, [migrationWatch.isPending, contentLoading, hasContent])

  const migrationNeedsReload =
    migrationWatch.isCompleted && shouldShowReloadRef.current

  useRouteTokenInvalidation({
    blocksContext,
    serverContentID,
    migrationNeedsReload,
    disableRouteToken
  })

  const clearMigrationReload = useCallback(() => {
    shouldShowReloadRef.current = false
  }, [])

  return {
    migrationIsPending: migrationWatch.isPending,
    migrationNeedsReload,
    clearMigrationReload
  }
}

export const useMigrationWatch = ({
  blocksContext,
  contentID
}: {
  blocksContext: BlocksContextType | null
  contentID?: string
}): MigrationWatchState => {
  const migrationDocumentID = resolveMigrationDocumentID(
    blocksContext,
    contentID
  )

  // Migrations are one-way. Once completed or failed, latch and unsubscribe from Firebase.
  const [settledState, setSettledState] = useState<{
    completed: boolean
  } | null>(null)
  useEffect(() => {
    setSettledState(null)
  }, [migrationDocumentID])

  const subscribePath =
    migrationDocumentID && settledState === null
      ? `${CONTENT_MIGRATION_ROOT}/${migrationDocumentID}`
      : ""

  const [contentMigration] = useFirebaseValue<{ status?: string } | null>(
    subscribePath ? `${subscribePath}/content` : ""
  )
  const [completionsMigration] = useFirebaseValue<{ status?: string } | null>(
    subscribePath ? `${subscribePath}/completions` : ""
  )
  const [completionsAbsent, setCompletionsAbsent] = useState(false)
  const completionsAbsentTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  useEffect(() => {
    if (completionsAbsentTimerRef.current) {
      clearTimeout(completionsAbsentTimerRef.current)
      completionsAbsentTimerRef.current = null
    }

    setCompletionsAbsent(false)

    if (!subscribePath) return
    if (completionsMigration !== null) return

    completionsAbsentTimerRef.current = setTimeout(() => {
      completionsAbsentTimerRef.current = null
      setCompletionsAbsent(true)
    }, COMPLETIONS_ABSENT_GRACE_MS)
  }, [subscribePath, completionsMigration])

  useEffect(
    () => () => {
      if (completionsAbsentTimerRef.current) {
        clearTimeout(completionsAbsentTimerRef.current)
        completionsAbsentTimerRef.current = null
      }
    },
    []
  )

  const contentStatus = contentMigration?.status ?? null
  const completionsStatus = completionsMigration?.status ?? null
  const hasMigrationRecord =
    contentMigration != null || completionsMigration != null
  const routeIsMigrating =
    blocksContext?.contentStatus === "migrating" && !contentID

  const isCompleted =
    settledState?.completed === true ||
    (contentStatus === STATUS_COMPLETED &&
      (completionsAbsent ||
        completionsStatus === STATUS_COMPLETED ||
        completionsStatus === STATUS_COMPLETIONS_NOT_NEEDED))

  const isFailed =
    contentStatus === STATUS_FAILED ||
    contentStatus === STATUS_TERMINAL_FAILED ||
    completionsStatus === STATUS_FAILED ||
    completionsStatus === STATUS_TERMINAL_FAILED

  const isRealtimePending =
    hasMigrationRecord &&
    !isFailed &&
    !isCompleted &&
    (contentStatus === STATUS_IN_PROGRESS ||
      contentStatus === STATUS_PENDING_CONTENT_MIGRATION ||
      completionsStatus === STATUS_IN_PROGRESS ||
      completionsStatus === STATUS_PENDING_CONTENT_MIGRATION ||
      contentStatus === STATUS_COMPLETED)

  const isPending = isRealtimePending || (routeIsMigrating && !isCompleted)

  useEffect(() => {
    if (settledState !== null) return
    if (isCompleted) setSettledState({ completed: true })
    else if (isFailed) setSettledState({ completed: false })
  }, [isCompleted, isFailed, settledState])

  return {
    migrationDocumentID,
    isPending: settledState !== null ? false : isPending,
    isCompleted
  }
}

const MigrationOverlayContentContainer = ({
  children
}: {
  children: ReactNode
}) => (
  <Box
    className="MigrationOverlay"
    minH={OVERLAY_CONTENT_MIN_HEIGHT}
    maxW={OVERLAY_CONTENT_MAX_WIDTH}
    w="100%"
    px={4}
    mx="auto"
    display="flex"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
  >
    <Box w="100%">{children}</Box>
  </Box>
)

/** Migration-specific loading UI (e.g. "Preparing content..."). Used when content is loading due to migration. */
export const MigrationLoadingView = () => {
  const { t } = useTranslate()
  return (
    <Box
      minH="300px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <VStack gap={3}>
        <Spinner size="lg" color="gray.600" />
        <Text color="gray.600" textAlign="center" fontSize="lg">
          {t(BLOCKS_PREPARING_CONTENT_KEY)}
        </Text>
      </VStack>
    </Box>
  )
}

/** Shared full-screen blocking shell for migration error and action-required UIs. */
const MigrationBlockingShell = ({
  children,
  containerProps = {}
}: {
  children: ReactNode
  containerProps?: BoxProps
}) => (
  <Box
    className="MigrationBlockingShell"
    position="absolute"
    inset={0}
    zIndex={1000}
    bg="rgba(255, 255, 255, 0.82)"
    display="flex"
    alignItems="center"
    justifyContent="center"
    pointerEvents="all"
    {...containerProps}
  >
    <MigrationOverlayContentContainer>
      {children}
    </MigrationOverlayContentContainer>
  </Box>
)

/**
 * Renders when blocks content cannot be shown due to staging environment unavailable (e.g. migration disabled).
 * Prevents rendering of content; use in the error branch, not as an overlay.
 */
export const MigrationStagingError = ({
  error,
  onReload
}: {
  error: any
  onReload: () => void
}) => (
  <MigrationBlockingShell
    containerProps={{
      position: "relative"
    }}
  >
    <StagingUnavailableContent error={error} onReload={onReload} />
  </MigrationBlockingShell>
)

type MigrationBlockingOverlayProps = {
  /** When true, show "reload to continue" overlay. */
  migrationNeedsReload: boolean
  /** In-app reload (refetch); no full page reload. */
  onReload: () => void
}

/**
 * Overlay shown when user had the document open and a migration completed—action required (reload).
 * Returns null when not needed. Only used in the content branch; errors are handled separately.
 */
export const MigrationBlockingOverlay = ({
  migrationNeedsReload,
  onReload
}: MigrationBlockingOverlayProps) => {
  if (!migrationNeedsReload) return null

  return (
    <MigrationBlockingShell
      containerProps={{
        css: {
          "& .MigrationOverlay": {
            alignItems: "flex-start",
            pt: "200px",
            height: "100%"
          }
        }
      }}
    >
      <VStack gap={3}>
        <Text>This content was updated. Reload to continue.</Text>
        <Button
          type="button"
          colorPalette="blue"
          borderRadius="full"
          onClick={onReload}
        >
          Reload document
        </Button>
      </VStack>
    </MigrationBlockingShell>
  )
}

function StagingUnavailableContent({
  error,
  onReload
}: {
  error: any
  onReload: () => void
}) {
  const { isSuperuser } = useBlocksConfig()
  const unavailableError = getGraphQLErrorByCode(
    error,
    ENVIRONMENT_UNAVAILABLE_ERROR_CODE
  )
  const adminPath =
    typeof unavailableError?.extensions?.adminPath === "string"
      ? unavailableError.extensions.adminPath
      : null
  const showAdminLink = isSuperuser && !!adminPath
  return (
    <VStack
      gap={showAdminLink ? 3 : 0}
      className="MigrationBlockingOverlay__content"
    >
      <Text textAlign="center">{STAGING_ENV_UNAVAILABLE_MESSAGE}</Text>
      {showAdminLink ? (
        <Link href={adminPath} color="blue.600" target="_blank">
          Enable migration for this Path or school
        </Link>
      ) : null}
    </VStack>
  )
}

/** Thin ribbon at top of blocks UI to show a non-blocking content notice (e.g. staging Firebase→Postgres). */
export const ContentNoticeRibbon = ({
  notice,
  onDismiss
}: {
  notice: string | null | undefined
  onDismiss?: () => void
}) => {
  const [dismissed, setDismissed] = useState(false)
  if (!notice || dismissed) return null
  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      zIndex={100}
      py={1.5}
      px={3}
      bg="blue.50"
      borderBottomWidth="1px"
      borderBottomColor="blue.100"
      fontSize="sm"
      color="blue.800"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
    >
      <Text flex={1} lineClamp={2}>
        {notice}
      </Text>
      <Button
        size="xs"
        variant="ghost"
        colorPalette="blue"
        onClick={() => {
          setDismissed(true)
          onDismiss?.()
        }}
      >
        Dismiss
      </Button>
    </Box>
  )
}
